import React from 'react';
import { useTaxForm, T } from '../TaxFormContext';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { AnimatePresence, motion } from 'framer-motion';
import { useLiveTaxEngine } from '../useLiveTaxEngine';

export const Step1AccountInfo: React.FC = () => {
  const { formData, updateFormData, lang } = useTaxForm();
  
  // Call the hook just to keep computed fields syncing if they modify things here
  useLiveTaxEngine();

  return (
    <div className="space-y-6 animate-fade-in relative z-10 w-full max-w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-white/60 backdrop-blur-sm p-4 md:p-6 rounded-3xl border border-slate-200/60 shadow-sm">
        <div className="space-y-2">
          <Label className="font-bold text-slate-700"><T en="Full Identity Name" ur="مکمل نام شناختی کارڈ کے مطابق (Full Name)"/></Label>
          <Input 
            value={formData.full_name || ''} 
            onChange={(e) => updateFormData({full_name: e.target.value})} 
            className="h-14 rounded-2xl bg-white/80 focus:bg-white transition-colors border-slate-200 focus:ring-primary/20" 
            placeholder={lang === 'ur' ? "مکمل نام" : "Full Name"}
          />
        </div>
        <div className="space-y-2">
          <Label className="font-bold text-slate-700"><T en="CNIC / NTN" ur="شناختی کارڈ نمبر / این ٹی این (CNIC / NTN)"/></Label>
          <Input 
            value={formData.cnic || ''} 
            onChange={(e) => updateFormData({cnic: e.target.value})} 
            placeholder="00000-0000000-0" 
            className="h-14 rounded-2xl bg-white/80 focus:bg-white transition-colors border-slate-200 focus:ring-primary/20" 
          />
        </div>
        <div className="space-y-2">
            <Label className="font-bold text-slate-700"><T en="Residential Status" ur="رہائشی حیثیت (Residential Status)"/></Label>
            <Select value={formData.residential_status || 'resident'} onValueChange={(v:any)=>updateFormData({residential_status:v})}>
              <SelectTrigger className="h-14 rounded-2xl bg-white/80 focus:bg-white transition-colors border-slate-200 font-bold focus:ring-primary/20"><SelectValue placeholder={lang === "ur" ? "حیثیت (Status)" : "Status"} /></SelectTrigger>
              <SelectContent className="rounded-2xl border-slate-200 shadow-xl">
                <SelectItem value="resident" className="rounded-xl focus:bg-slate-50"><T en="Resident of Pakistan" ur="پاکستان کا رہائشی (Resident)"/></SelectItem>
                <SelectItem value="non_resident" className="rounded-xl focus:bg-slate-50"><T en="Non-Resident" ur="غیر ملکی رہائشی (Non-Resident)"/></SelectItem>
              </SelectContent>
            </Select>
        </div>
        <div className="space-y-2">
          <Label className="font-bold text-slate-700"><T en="Email Address" ur="ای میل ایڈریس (Email Address)"/></Label>
          <Input 
            value={formData.email || ''} 
            onChange={(e) => updateFormData({email: e.target.value})} 
            placeholder="name@example.com" 
            className="h-14 rounded-2xl bg-white/80 focus:bg-white transition-colors border-slate-200 focus:ring-primary/20" 
          />
        </div>
      </div>
      
      {/* FBR CREDENTIALS */}
      <div className="bg-gradient-to-br from-indigo-50/80 to-blue-50/50 backdrop-blur-sm border border-indigo-100/60 p-6 md:p-8 rounded-[2rem] shadow-sm space-y-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 p-8 opacity-[0.03] transform scale-150 pointer-events-none">
           <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-64 h-64"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
        </div>
        <div className="flex items-center justify-between relative z-10">
            <div className="space-y-1 pr-4">
              <Label className="font-black text-lg text-indigo-900 tracking-tight"><T en="Active FBR / AJK IRIS Account" ur="فعال ایف بی آر اکاؤنٹ (Active FBR IRIS)"/></Label>
              <p className="text-sm text-indigo-700/80 font-medium md:max-w-md leading-relaxed"><T en="Do you already hold credentials for the official Federal Board portals?" ur="کیا آپ کا ایف بی آر (FBR) کا پاس ورڈ موجود ہے؟"/></p>
            </div>
            <Switch checked={!!formData.has_fbr_account} onCheckedChange={(v)=>updateFormData({has_fbr_account: v})} className="data-[state=checked]:bg-indigo-600"/>
        </div>
        
        <AnimatePresence>
            {formData.has_fbr_account && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-6 border-t border-indigo-100/60 mt-4 relative z-10 overflow-hidden">
                <div className="space-y-2">
                  <Label className="font-bold text-indigo-900"><T en="IRIS ID (CNIC)" ur="آئی آر آئی ایس آئی ڈی (IRIS ID)"/></Label>
                  <Input value={formData.fbr_username || ''} onChange={(e) => updateFormData({fbr_username: e.target.value})} className="h-14 rounded-2xl bg-white/90 border-white/50 shadow-sm focus-visible:ring-indigo-500 transition-all" placeholder="00000-0000000-0" />
                </div>
                <div className="space-y-2">
                  <Label className="font-bold text-indigo-900"><T en="IRIS Password / PIN" ur="پاس ورڈ یا پن (IRIS Password/PIN)"/></Label>
                  <Input type="password" value={formData.fbr_password || ''} onChange={(e) => updateFormData({fbr_password: e.target.value})} className="h-14 rounded-2xl bg-white/90 border-white/50 shadow-sm focus-visible:ring-indigo-500 transition-all" placeholder="••••••••" />
                </div>
              </motion.div>
            )}
        </AnimatePresence>
      </div>
    </div>
  );
};